import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { push } from 'connected-react-router';
import { DataTable, DataTableColumn, TableActions } from '@saucelabs/ds-table';
import { Pagination } from '@saucelabs/ds-pagination';
import { modalActions } from '@saucelabs/sl-container-modal';
import { appStorageService } from '@saucelabs/sl-util-axios';
import { records as appStorageRecords, constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';
import { Loader } from '@saucelabs/sl-component-loader';
import {
  IconDownloadFill,
  IconManualFill,
  IconTrashFill,
} from '@saucelabs/ds-icons';

import globalConstants from '../../../constants';
import constants from '../constants';
import TableCellName from './TableCellName';
import TableCellIdentifier from './TableCellIdentifier';
import TableCellDescription from './TableCellDescription';
import TableCellUploaded from './TableCellUploaded';
import DeleteModal from './DeleteModal';
import actions, { FetchFilesArguments } from '../actions';
import selectors from '../selectors';
import { DetailsPageParams } from '../../../routes';

const {
  APP_STORAGE_KIND,
  APP_STORAGE_LIFETIME_DAYS,
} = appStorageConstants;

type RowData = {
  name: string;
  id: string;
  description: string;
  version: string;
  build: string;
  uploaded: Date;
  file: appStorageRecords.AppStorageFile;
};

const Table:React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const [
    fileSelectedForDeletion,
    setFileSelectedForDeletion,
  ] = useState<appStorageRecords.AppStorageFile | null>(null);

  const {
    groupId,
  } = useParams<DetailsPageParams>();

  const {
    currentPage,
    files,
    isLoading,
    itemsPerPage,
    kind,
    totalItems,
  } = useSelector(selectors.filesSelector);

  const fetchFiles = useCallback((fetchFilesArguments: FetchFilesArguments = {}) => {
    void dispatch(actions.fetchFiles(fetchFilesArguments));
  }, [dispatch]);

  useEffect(() => {
    fetchFiles({ groupId });
  }, [fetchFiles, groupId]);

  const columns: DataTableColumn<RowData>[] = useMemo(() => (
    [
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellName file={row.file} />),
        field: 'name',
        key: 'name',
        name: 'File Name',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellIdentifier file={row.file} />),
        field: 'id',
        key: 'id',
        name: 'File ID',
      },
      {
        allowsSorting: false, // alphabetical sorting isn't helpful here
        field: 'version',
        helpText: (() => {
          if (kind) {
            if (kind === APP_STORAGE_KIND.IOS) {
              return 'The value of "CFBundleShortVersionString" in Info.plist';
            }
            if (kind === APP_STORAGE_KIND.ANDROID) {
              return 'The value of "versionName" in the app manifest';
            }
          }
          return '';
        })(),
        key: 'version',
        name: 'Version',
      },
      {
        allowsSorting: false, // alphabetical sorting isn't helpful here
        name: 'Build',
        field: 'build',
        key: 'build',
        helpText: (() => {
          if (kind) {
            if (kind === APP_STORAGE_KIND.IOS) {
              return 'The value of "CFBundleVersion" in Info.plist';
            }
            if (kind === APP_STORAGE_KIND.ANDROID) {
              return 'The value of "versionCode" in the app manifest';
            }
          }
          return '';
        })(),
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellDescription file={row.file} />),
        field: 'description',
        key: 'description',
        name: 'Description',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellUploaded file={row.file} />),
        field: 'uploaded',
        helpText: `Uploaded files will expire after ${APP_STORAGE_LIFETIME_DAYS} days`,
        key: 'uploaded',
        name: 'Uploaded',
      },
      {
        allowsSorting: false,
        customBodyRender: (value, key, row) => (
          <div className="w-14">
            <TableActions actions={[
              {
                label: 'Start Test',
                onPress: () => dispatch(push(`/live/app-testing/file/${row.file.id}`)),
                icon: IconManualFill,
                primaryAction: true,
              },
              {
                label: 'Download',
                onPress: () => {
                  window.location.href = `${appStorageService.defaults.baseURL ?? ''}/v1/storage/download/${row.file.id}`;
                },
                icon: IconDownloadFill,
                primaryAction: true,
              },
              {
                label: 'Delete',
                onPress: () => {
                  setFileSelectedForDeletion(row.file);
                  void dispatch(
                    modalActions.showModal(constants.DELETE_FILE_MODAL_IDENTIFIER),
                  );
                },
                icon: IconTrashFill,
                primaryAction: true,
              },
            ]}
            />
          </div>
        ),
        field: 'id',
        key: 'actions',
        name: '',
      },
    ]
  ), [dispatch, kind]);

  const rows = useMemo(
    () => (
      files.map((file) => ({
        name: file?.name,
        id: file?.id,
        description: file?.description,
        version: file?.getVersion(),
        build: file?.getBuild(),
        uploaded: file?.getCreationDate(),
        file,
      }))),
    [files],
  );

  const showUpdateLoader = isLoading && files.length > 0;

  return (
    <>
      <DeleteModal file={fileSelectedForDeletion} />
      <div className="relative">
        {showUpdateLoader && (
          <div className="absolute w-full h-screen z-10 flex items-center justify-center bg-opacity-80 bg-white">
            <Loader show />
          </div>
        )}
        <Loader
          className="my-2 mx-auto"
          show={isLoading && !files.length}
        >
          <div className="overflow-auto">
            <div className="min-w-128">
              <DataTable
                aria-label="App Management Files Table"
                columns={columns}
                data-qa="app-management-files-table"
                disableTypeAhead
                isPaginated={false} // DataTable's pagination is currently insufficient for our needs
                rows={rows}
              />
            </div>
          </div>
          {(totalItems > globalConstants.TABLE_PAGE_SIZES[0]) && (
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              itemsPerPageOptions={[...globalConstants.TABLE_PAGE_SIZES]}
              onItemsPerPageChange={(newItemsPerPage) => (
                fetchFiles({
                  entriesPerPage: newItemsPerPage,
                  paginationPage: 1,
                })
              )}
              onPageChange={(newPageNumber) => (
                fetchFiles({
                  paginationPage: newPageNumber,
                })
              )}
              totalItems={totalItems}
            />
          )}
        </Loader>
      </div>
    </>
  );
};

export default Table;
